import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { settingsStyles } from '../../styles/settings-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { settingsService } from '../../services/settings-service.js';

export class TwilioSettings extends LitElement {
  static properties = {
    _twilioSid: { state: true },
    _twilioToken: { state: true },
    _twilioKey: { state: true },
    _twilioSecret: { state: true },
    _showPasswords: { type: Object, state: true },
    _saving: { state: true },
    _testing: { state: true },
    _twilioStatus: { state: true },
    _twilioTestResult: { state: true }
  };

  static styles = [designSystem, componentStyles, settingsStyles];

  constructor() {
    super();
    this._twilioSid = '';
    this._twilioToken = '';
    this._twilioKey = '';
    this._twilioSecret = '';
    this._showPasswords = { twilioSid: false, twilioToken: false, twilioKey: false, twilioSecret: false };
    this._saving = false;
    this._testing = false;
    this._twilioStatus = 'idle';
    this._twilioTestResult = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this._load();
  }

  async _load() {
    try {
      const data = await settingsService.loadTwilioConfig();
      if (data.success && data.config) {
        this._twilioSid = data.config.voice_twilio_account_sid || '';
        this._twilioToken = data.config.voice_twilio_auth_token || '';
        this._twilioKey = data.config.voice_twilio_api_key || '';
        this._twilioSecret = data.config.voice_twilio_api_secret || '';
      }
    } catch (err) {
      console.error('[TwilioSettings] Load failed:', err);
    }
  }

  _togglePassword(field) {
    this._showPasswords = { ...this._showPasswords, [field]: !this._showPasswords[field] };
  }

  async _testConnection() {
    if (this._testing) return;
    if (!this._twilioSid || !this._twilioToken) {
      this.dispatchEvent(new CustomEvent('toast', { detail: { message: 'Enter Account SID and Auth Token first', type: 'error' }, bubbles: true, composed: true }));
      return;
    }

    this._testing = true;
    this._twilioStatus = 'idle';
    this._twilioTestResult = null;

    try {
      const data = await settingsService.testTwilioConnection(this._twilioSid, this._twilioToken);
      if (data.success) {
        this._twilioStatus = 'success';
        this._twilioTestResult = data.account;
        this.dispatchEvent(new CustomEvent('toast', { detail: { message: `Connected: ${data.account?.friendly_name || 'Twilio account verified'}` }, bubbles: true, composed: true }));
      } else {
        this._twilioStatus = 'error';
        this.dispatchEvent(new CustomEvent('toast', { detail: { message: data.error || 'Connection failed', type: 'error' }, bubbles: true, composed: true }));
      }
    } catch (err) {
      this._twilioStatus = 'error';
      this.dispatchEvent(new CustomEvent('toast', { detail: { message: 'Network error testing connection', type: 'error' }, bubbles: true, composed: true }));
    } finally {
      this._testing = false;
      setTimeout(() => { this._twilioStatus = 'idle'; this._twilioTestResult = null; }, 10000);
    }
  }

  async _save() {
    if (this._saving) return;
    this._saving = true;

    try {
      const payload = {
        voice_twilio_account_sid: this._twilioSid,
        voice_twilio_auth_token: this._twilioToken,
      };
      if (this._twilioKey) payload.voice_twilio_api_key = this._twilioKey;
      if (this._twilioSecret) payload.voice_twilio_api_secret = this._twilioSecret;

      const data = await settingsService.saveTwilioConfig(payload);
      if (data.success) {
        this.dispatchEvent(new CustomEvent('toast', { detail: { message: data.message || 'Twilio settings saved' }, bubbles: true, composed: true }));
        this.dispatchEvent(new CustomEvent('settings-saved', { bubbles: true, composed: true }));
      } else {
        this.dispatchEvent(new CustomEvent('toast', { detail: { message: data.error || 'Save failed', type: 'error' }, bubbles: true, composed: true }));
      }
    } catch (err) {
      this.dispatchEvent(new CustomEvent('toast', { detail: { message: 'Network error saving settings', type: 'error' }, bubbles: true, composed: true }));
    } finally {
      this._saving = false;
    }
  }

  render() {
    return html`
      ${iconStyles}
      <div class="content-header">
        <h2 class="content-title">Twilio Configuration</h2>
        <p class="content-description">Connect your Twilio account to enable phone call functionality. Find your credentials in the Twilio Console.</p>
      </div>

      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp-duotone fa-solid fa-key section-icon"></i>
          <span class="section-title">Account Credentials</span>
        </div>
        <p class="section-description">Enter your Twilio Account SID and Auth Token from the Twilio Console Dashboard.</p>

        <div class="form-grid">
          ${this._renderPasswordField('twilioSid', 'Account SID', 'ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx', this._twilioSid, v => this._twilioSid = v, 'Your Twilio Account SID (starts with AC).')}
          ${this._renderPasswordField('twilioToken', 'Auth Token', 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx', this._twilioToken, v => this._twilioToken = v, 'Keep this secret. Never share your Auth Token.')}
          ${this._renderPasswordField('twilioKey', 'API Key', 'SKxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx', this._twilioKey, v => this._twilioKey = v, 'Optional API Key for enhanced security (starts with SK).')}
          ${this._renderPasswordField('twilioSecret', 'API Secret', 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx', this._twilioSecret, v => this._twilioSecret = v, 'API Secret corresponding to the API Key above.')}
        </div>
      </div>

      <div class="settings-section">
        <div class="section-title-row">
          <i class="fa-sharp-duotone fa-solid fa-plug section-icon"></i>
          <span class="section-title">Connection Test</span>
        </div>
        <p class="section-description">Verify that your credentials are valid and the connection to Twilio is working.</p>

        <div style="display:flex;align-items:center;gap:var(--space-4);margin-top:var(--space-3);">
          <button class="btn btn-secondary" @click=${this._testConnection} ?disabled=${this._testing}>
            ${this._testing
              ? html`<i class="fa-sharp fa-light fa-spinner-third" style="animation:spin 0.6s linear infinite;"></i> Testing...`
              : html`<i class="fa-sharp fa-light fa-plug"></i> Test Connection`
            }
          </button>
          <span class="connection-status ${this._twilioStatus}">
            ${this._twilioStatus === 'success' ? html`
              <i class="fa-sharp fa-light fa-check-circle"></i> Connected${this._twilioTestResult?.friendly_name ? ` — ${this._twilioTestResult.friendly_name}` : ''}
            ` : ''}
            ${this._twilioStatus === 'error' ? html`
              <i class="fa-sharp fa-light fa-circle-xmark"></i> Connection failed
            ` : ''}
            ${this._twilioStatus === 'idle' ? html`
              <i class="fa-sharp fa-light fa-circle-minus"></i> Not tested yet
            ` : ''}
          </span>
        </div>
      </div>

      <div class="actions-bar">
        <button class="btn btn-primary" @click=${this._save} ?disabled=${this._saving}>
          ${this._saving
            ? html`<i class="fa-sharp fa-light fa-spinner-third" style="animation:spin 0.6s linear infinite;"></i> Saving...`
            : html`<i class="fa-sharp fa-light fa-floppy-disk"></i> Save Changes`
          }
        </button>
      </div>
    `;
  }

  _renderPasswordField(id, label, placeholder, value, onChange, hint) {
    const show = this._showPasswords[id];
    return html`
      <div class="form-group">
        <label class="form-label">${label}</label>
        <div class="password-wrap">
          <input
            type="${show ? 'text' : 'password'}"
            class="form-input"
            placeholder="${placeholder}"
            .value=${value}
            @input=${e => onChange(e.target.value)}
          />
          <button class="password-toggle" @click=${() => this._togglePassword(id)}>
            <i class="fa-sharp fa-light ${show ? 'fa-eye-slash' : 'fa-eye'}"></i>
          </button>
        </div>
        <div class="form-hint">${hint}</div>
      </div>
    `;
  }
}

customElements.define('twilio-settings', TwilioSettings);
